/**
 * Square icon button that copies a list of keyword strings to the
 * system clipboard, one per line. Surfaces only when ≥1 keyword is
 * selected on the Keywords tab — placed next to FilterMenuButton in
 * the top-of-page strip.
 */

import { useState } from "react";
import { I } from "@/components/Icon";

export function CopyKeywordsButton({
  getKeywordTexts,
  count,
}: {
  /** Lazy callback so the parent doesn't have to recompute the joined
   *  string on every render — only when the user clicks. */
  getKeywordTexts: () => string[];
  count: number;
}) {
  const [copied, setCopied] = useState(false);

  const onClick = async () => {
    const texts = getKeywordTexts().filter(Boolean);
    if (texts.length === 0) return;
    try {
      await navigator.clipboard.writeText(texts.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard API not granted — drop a basic prompt() fallback so
      // the user can still grab the text manually.
      window.prompt("Copy selected keywords", texts.join("\n"));
    }
  };

  return (
    <button
      type="button"
      className="btn icon"
      onClick={() => void onClick()}
      title={
        copied
          ? `Copied ${count} keyword${count === 1 ? "" : "s"}`
          : `Copy selected keyword${count === 1 ? "" : "s"}`
      }
      aria-label="Copy selected keywords"
    >
      {copied ? I.check : I.copy}
    </button>
  );
}
